import { AfterViewInit, Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Params, Router } from '@angular/router';
import Handsontable from 'handsontable';
import { forkJoin, lastValueFrom } from 'rxjs';
import { GenericMessageComponent } from '../dialogs/generic-message/generic-message.component';
import { UploadFile } from '../documents/upload-file';
import { Entity, EntityKey } from '../entity/entity';
import { BrandID } from '../enums/brand-id';
import { TagFieldUpdateResult } from '../model/tag-field-update-result';
import { TagGridRow } from '../model/tag-grid-row';
import { TagInformation } from '../model/tag-information';
import { UnitTaggingRow } from '../model/unit-tagging-row';
import { DocumentService } from '../services/document-service';
import { EntityService } from '../services/entity-service';
import { TagService } from '../services/tag-service';
import { MessageLevel, TagSaveResponse } from '../tags/tag-save-response';
import { EntityUtils } from '../utils/entity-utils';
import { FileUtils } from '../utils/file-utils';
import { StringUtils } from '../utils/string-utils';
import { SetTagFieldDialogComponent } from './set-tag-field-dialog/set-tag-field-dialog.component';
import { TagPreviewDialogComponent } from './tag-preview-dialog/tag-preview-dialog.component';
import { UnitTaggingHelpDialogComponent } from './unit-tagging-help-dialog/unit-tagging-help-dialog.component';

@Component({
  selector: 'app-unit-tagging',
  templateUrl: './unit-tagging.component.html',
  styleUrls: ['./unit-tagging.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, MatProgressSpinner],
})
export class UnitTaggingComponent implements OnChanges, OnInit, AfterViewInit {
  private readonly router = inject(Router);
  private readonly tagService = inject(TagService);
  private readonly entityService = inject(EntityService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly documentService = inject(DocumentService);
  private readonly overwriteFields = ['tag', 'riser', 'floor', 'room', 'building', 'poLine'];
  private handsontable!: Handsontable;
  readonly entityKey = signal<EntityKey>(undefined!);
  readonly gridData = signal<TagGridRow[]>([]);
  readonly shipEligibleCount = signal(0);
  readonly hiRiseEligibleCount = signal(0);
  readonly includeShippingTags = signal(false);
  readonly isReadOnly = signal(false);
  private readonly entity = signal<Entity>(undefined!);
  readonly isBusy = signal(false);
  readonly errorMessage = signal<string>(undefined!);
  readonly messages = signal<string[]>([]);
  readonly showWarningOverride = signal(false);
  private readonly columnKeys = [
    'lineId',
    'partNumber',
    'sequence',
    'reference',
    'location',
    'tag',
    'riser',
    'floor',
    'room',
    'building',
    'poLine',
    'size',
    'voltage',
    'poNum',
    'projName',
    'noTag',
  ];
  private readonly filterableKeys = ['lineId', 'partNumber', 'reference', 'location', 'tag', 'riser', 'floor', 'room', 'building', 'poLine'];
  private readonly booleanKeys = ['size', 'voltage', 'poNum', 'projName', 'noTag'];
  private readonly tagKeys = ['reference', 'location', 'size', 'voltage', 'poNum', 'projName', 'noTag'];
  private readonly allColHeaders = [
    'Line #',
    'Part #',
    'Seq',
    'Reference',
    'Location',
    'Tag',
    'Riser',
    'Floor',
    'Room',
    'Bldg',
    'PO Line',
    'Size',
    'Voltage',
    'PO #',
    'Project',
    'No Tag',
  ];
  readonly entityName = input.required<string>({ alias: 'entity-name' });
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });
  private visibleColumnKeys: string[] = this.columnKeys.slice();
  private activeFilterKey!: string;
  private activeFilterOverlay!: string;
  filterValues: Record<string, string> = {};

  private readRouteParams(): void {
    const entityName: string = this.entityName();
    const idString: string = this.entityId();
    const revisionString: string = this.entityRevision();
    if (StringUtils.isNotBlank(entityName) && StringUtils.isNotBlank(idString) && !StringUtils.isEmptyNumericValue(+revisionString)) {
      this.entityKey.set(new EntityKey());
      this.entityKey().entityId = idString;
      this.entityKey().entityName = entityName;
      this.entityKey().entityRevision = +revisionString;
    } else {
      this.goBack();
    }
  }

  ngOnInit(): void {
    this.readRouteParams();
    this.loadUnitTags();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.readRouteParams();
    }
  }

  ngAfterViewInit(): void {
    this.buildTable();
  }

  private loadUnitTags(): void {
    if (this.entityKey()) {
      this.isBusy.set(true);
      this.errorMessage.set(undefined!);
      forkJoin({
        units: this.tagService.getUnits(this.entityKey()),
        entity: this.entityService.getEntityByKey(this.entityKey()),
      }).subscribe({
        next: (result: { units: UnitTaggingRow[]; entity: Entity }) => this.loadTaggingData(result.units, result.entity),
        error: (error) => this.handleError(error),
      });
    }
  }

  private loadTaggingData(rows: UnitTaggingRow[], entity: Entity): void {
    this.entity.set(entity);
    this.includeShippingTags.set(entity ? entity.includeShippingTags : false);
    this.isReadOnly.set(entity ? entity.frozen || entity.approvalStatus.indexOf('Approved') >= 0 : false);
    this.loadGridData(rows);
    this.applyTagColumnVisibility();
  }

  private loadGridData(rows: UnitTaggingRow[]): void {
    this.gridData.set((rows || []).map((row) => this.buildGridRow(row)));
    this.updateCounts();
    this.refreshTable();
    this.isBusy.set(false);
  }

  private buildGridRow(row: UnitTaggingRow): TagGridRow {
    const tag: TagInformation = row.tagInformation || new TagInformation();
    return {
      lineId: row.lineId,
      partNumber: row.lineDescription,
      lineDescription: row.partNumber,
      sequence: tag.sequence,
      reference: tag.reference,
      location: tag.location,
      tag: tag.tag,
      riser: tag.riser,
      floor: tag.floor,
      room: tag.room,
      building: tag.building,
      poLine: tag.poLine,
      size: tag.size,
      voltage: tag.voltage,
      poNum: tag.poNum,
      projName: tag.projName,
      noTag: tag.noTag,
    };
  }

  private buildTable(): void {
    const element = document.getElementById('unitTaggingTable');
    this.handsontable = new Handsontable(element!, this.buildTableOptions());
    this.handsontable.addHook('beforeAutofill', [(start: any, end: any, data: any) => this.beforeAutofill(start, end, data)]);
    this.handsontable.addHook('beforeChange', [(changes: any, source: any) => this.beforeTagChange(changes, source)]);
    this.handsontable.addHook('afterChange', [(changes: any, source: any) => this.afterChange(changes, source)]);
  }

  private buildTableOptions(): any {
    const visible = this.computeVisibleColumns();
    return {
      data: this.gridData(),
      colHeaders: visible.colHeaders,
      columns: visible.columns,
      colWidths(index: number) {
        const narrowCols: number[] = [0, 2];
        const checkBoxCols: number[] = [11, 12, 13, 14, 15];
        const wideCols: number[] = [1];
        if (narrowCols.indexOf(index) >= 0) {
          return 70;
        } else if (checkBoxCols.indexOf(index) >= 0) {
          return 80;
        } else if (wideCols.indexOf(index) >= 0) {
          return 240;
        }
        return 140;
      },
      columnSorting: true,
      fillHandle: { direction: 'vertical', autoInsertRow: false },
      allowInsertRow: false,
      allowRemoveRow: false,
      allowInsertColumn: false,
      allowRemoveColumn: false,
      columnHeaderHeight: 42,
      fixedColumnsLeft: 3,
      afterGetColHeader: (col: any, th: any) => this.decorateHeaderCell(col, th),
      height: 620,
      stretchH: 'none',
    };
  }

  private allColumnDefs(): any[] {
    return [
      { data: 'lineId', readOnly: true, className: 'htCenter' },
      { data: 'partNumber', readOnly: true, className: 'htCenter' },
      { data: 'sequence', readOnly: true, type: 'numeric', className: 'htCenter' },
      { data: 'reference', validator: (value: any, callback: any) => this.validateLength(value, callback, 24, 'Reference'), className: 'htCenter' },
      { data: 'location', validator: (value: any, callback: any) => this.validateLength(value, callback, 16, 'Location'), className: 'htCenter' },
      { data: 'tag', validator: (value: any, callback: any) => this.validateLength(value, callback, 14, 'Tag'), className: 'htCenter' },
      { data: 'riser', validator: (value: any, callback: any) => this.validateLength(value, callback, 20, 'Riser'), className: 'htCenter' },
      { data: 'floor', validator: (value: any, callback: any) => this.validateLength(value, callback, 5, 'FL'), className: 'htCenter' },
      { data: 'room', validator: (value: any, callback: any) => this.validateLength(value, callback, 20, 'Room'), className: 'htCenter' },
      { data: 'building', validator: (value: any, callback: any) => this.validateLength(value, callback, 20, 'BLD'), className: 'htCenter' },
      { data: 'poLine', validator: (value: any, callback: any) => this.validateLength(value, callback, 5, 'POL'), className: 'htCenter' },
      { data: 'size', type: 'checkbox', className: 'htCenter' },
      { data: 'voltage', type: 'checkbox', className: 'htCenter' },
      { data: 'poNum', type: 'checkbox', className: 'htCenter' },
      { data: 'projName', type: 'checkbox', className: 'htCenter' },
      { data: 'noTag', type: 'checkbox', className: 'htCenter' },
    ];
  }

  private computeVisibleColumns(): { colHeaders: string[]; columns: any[] } {
    const allColumns = this.allColumnDefs();
    const colHeaders: string[] = [];
    const columns: any[] = [];
    const keys: string[] = [];
    this.columnKeys.forEach((key, index) => {
      if (this.includeShippingTags() || !this.tagKeys.includes(key)) {
        keys.push(key);
        colHeaders.push(this.allColHeaders[index]);
        columns.push(allColumns[index]);
      }
    });
    this.visibleColumnKeys = keys;
    return { colHeaders, columns };
  }

  private applyTagColumnVisibility(): void {
    if (this.handsontable) {
      const visible = this.computeVisibleColumns();
      this.handsontable.updateSettings({ colHeaders: visible.colHeaders, columns: visible.columns }, false);
      this.applyFilters();
    }
  }

  onIncludeShippingTagsChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.includeShippingTags.set(checkbox.checked);
    this.applyTagColumnVisibility();
  }

  validateLength(value: string, callback: any, maxLength: number, fieldName: string): void {
    const isValid: boolean = (value?.length || 0) <= maxLength;
    if (isValid) {
      this.errorMessage.set(undefined!);
    } else {
      this.errorMessage.set(fieldName + ' can only be ' + maxLength + ' characters long');
    }
    callback(isValid);
  }

  private beforeTagChange(changes: any[], source: string): boolean {
    let allowed = true;
    if (source === 'Autofill.fill') {
      allowed = false;
    } else if (changes && source !== 'autofill-override') {
      const conflicts = changes.filter((change) => this.isOverwriteConflict(change));
      if (conflicts.length > 0) {
        allowed = confirm(conflicts.length + ' unit(s) already have a configurator value. Replace the existing value(s)?');
      }
    }
    return allowed;
  }

  private isOverwriteConflict(change: any[]): boolean {
    const field: string = change[1];
    const oldValue = change[2];
    const newValue = change[3];
    return this.overwriteFields.includes(field) && StringUtils.isNotBlank(oldValue) && oldValue !== newValue;
  }

  private beforeAutofill(start: any, end: any, data: any[][]): boolean {
    let handled = false;
    if (data && data.length > 0) {
      const sourceLineId = this.handsontable.getDataAtCell(start.row, 0);
      const valueToCopy = (data[0][0] ?? '').toString();
      for (let column = start.col; column <= end.col; column++) {
        for (let row = start.row; row <= end.row; row++) {
          if (this.handsontable.getDataAtCell(row, 0) === sourceLineId) {
            this.handsontable.setDataAtCell(row, column, valueToCopy, 'autofill-override');
          }
        }
      }
      handled = true;
    }
    return handled;
  }

  private afterChange(changes: any[], source: string): void {
    if (changes && source !== 'loadData') {
      this.updateCounts();
    }
  }

  private updateCounts(): void {
    this.shipEligibleCount.set(this.gridData().filter((row) => !row.noTag).length);
    this.hiRiseEligibleCount.set(this.gridData().filter((row) => !row.noTag && this.isHiRise(row)).length);
  }

  private isHiRise(row: TagGridRow): boolean {
    return /SM|SG/.test(row.partNumber || '');
  }

  private decorateHeaderCell(col: number, th: HTMLElement): void {
    const existing = th.querySelector('.header-control');
    if (existing) {
      existing.remove();
    }
    const key = this.visibleColumnKeys[col];
    if (this.filterableKeys.includes(key)) {
      th.appendChild(this.buildFilterInput(key));
    } else if (this.booleanKeys.includes(key)) {
      th.appendChild(this.buildCheckAll(key));
    }
  }

  private buildFilterInput(key: string): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'header-control col-filter';
    input.setAttribute('data-key', key);
    input.value = this.filterValues[key] || '';
    input.style.display = 'block';
    input.style.width = '90%';
    input.style.margin = '2px auto 0';
    input.style.padding = '2px 4px';
    input.style.boxSizing = 'border-box';
    input.addEventListener('mousedown', (event) => event.stopPropagation());
    input.addEventListener('click', (event) => event.stopPropagation());
    input.addEventListener('focus', () => this.handsontable.deselectCell());
    input.addEventListener('keydown', (event) => event.stopPropagation());
    input.addEventListener('keyup', () => this.onHeaderFilter(key, input));
    return input;
  }

  private buildCheckAll(key: string): HTMLInputElement {
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'header-control check-all';
    checkbox.checked = this.allChecked(key);
    checkbox.title = 'Check or uncheck all filtered rows';
    checkbox.style.display = 'block';
    checkbox.style.margin = '4px auto 0';
    checkbox.addEventListener('mousedown', (event) => event.stopPropagation());
    checkbox.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleAll(key);
    });
    return checkbox;
  }

  private onHeaderFilter(key: string, input: HTMLInputElement): void {
    this.filterValues[key] = input.value;
    this.activeFilterKey = key;
    this.activeFilterOverlay = input.closest('.ht_clone_top_left_corner') ? 'ht_clone_top_left_corner' : 'ht_clone_top';
    this.applyFilters();
    this.restoreFilterFocus();
  }

  private currentFilteredRows(): TagGridRow[] {
    const active = this.filterableKeys.filter((key) => StringUtils.isNotBlank(this.filterValues[key]));
    return this.gridData().filter((row) => active.every((key) => this.matchesFilter(row, key)));
  }

  applyFilters(): void {
    if (this.handsontable) {
      this.handsontable.loadData(this.currentFilteredRows());
    }
  }

  private matchesFilter(row: TagGridRow, key: string): boolean {
    const cellValue = ((row as any)[key] ?? '').toString().toLowerCase();
    return cellValue.includes(this.filterValues[key].toLowerCase());
  }

  private allChecked(key: string): boolean {
    const rows = this.currentFilteredRows();
    return rows.length > 0 && rows.every((row) => (row as any)[key]);
  }

  private toggleAll(key: string): void {
    const newValue = !this.allChecked(key);
    this.currentFilteredRows().forEach((row) => ((row as any)[key] = newValue));
    this.applyFilters();
    this.updateCounts();
  }

  private restoreFilterFocus(): void {
    if (this.activeFilterKey) {
      const key = this.activeFilterKey;
      const overlay = this.activeFilterOverlay || 'ht_clone_top';
      setTimeout(() => {
        const input = document.querySelector('#unitTaggingTable .' + overlay + ' input.col-filter[data-key="' + key + '"]') as HTMLInputElement;
        if (input && document.activeElement !== input) {
          input.focus();
          input.setSelectionRange(input.value.length, input.value.length);
        }
      });
    }
  }

  private refreshTable(): void {
    this.applyFilters();
  }

  // Mirrors EntitySaveUtils.throwTagUpdateExceptionIfEntered on the backend
  isTagLocked(): boolean {
    return ['Entered', 'Shipped', 'PartialShp', 'DbWait'].includes(this.entity()?.erpStatus);
  }

  save(ignoreWarnings: boolean): void {
    if (this.isReadOnly()) {
      this.showTagUpdateWarningDialog().then(() => this.performSave(ignoreWarnings));
    } else {
      this.performSave(ignoreWarnings);
    }
  }

  private async showTagUpdateWarningDialog(): Promise<void> {
    const messages: string[] = [];
    messages.push('This order is in the manufacturing phase of order fulfillment.');
    messages.push('There is a chance some units have already been packaged and shipped.');
    messages.push(this.getBrandName() + ' cannot guarantee that changes to the tags of this order will be completed');
    messages.push(' as requested since the order is already in process in our manufacturing department');
    const messageDialog = this.dialog.open(GenericMessageComponent, { maxWidth: '90vw', disableClose: true, data: { messages: messages } });
    await lastValueFrom(messageDialog.afterClosed());
  }

  private performSave(ignoreWarnings: boolean): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.messages.set([]);
    this.showWarningOverride.set(false);
    const overTaggedStockLines: string[] = this.stockLinesWithExtraTags();
    if (overTaggedStockLines.length > 0) {
      this.handleError('Quick Stock and Quick Deal lines are one unit each and take one tag. Line(s) with more than one tag: ' + overTaggedStockLines.join(', '));
    } else {
      const rows: UnitTaggingRow[] = this.gridData().map((row) => this.toUnitTaggingRow(row));
      this.tagService.saveUnits(this.entityKey(), ignoreWarnings, this.includeShippingTags(), rows).subscribe({
        next: (response: TagSaveResponse) => this.handleSaveResponse(response),
        error: (error) => this.handleError(error),
      });
    }
  }

  private stockLinesWithExtraTags(): string[] {
    const entity: Entity = this.entity();
    return (entity?.lines || [])
      .filter((line) => EntityUtils.isStockUnitLine(entity, line))
      .map((line) => line.lineId)
      .filter((lineId) => this.gridData().filter((row) => row.lineId === lineId).length > 1);
  }

  private getBrandName(): string {
    if (this.entity() && this.entity().brandID === BrandID.IEC) {
      return 'IEC';
    } else {
      return 'ClimateMaster';
    }
  }

  private handleSaveResponse(response: TagSaveResponse): void {
    this.isBusy.set(false);
    if (response.level === MessageLevel.WARNING) {
      this.messages.set(response.messages || []);
      this.showWarningOverride.set(true);
    } else if (response.level === MessageLevel.ERROR) {
      this.errorMessage.set((response.messages || []).join(' '));
    } else {
      this.snackBar.open('Tags Saved Successfully', 'Close', {
        duration: 5000,
      });
      this.loadUnitTags();
    }
  }

  private toUnitTaggingRow(row: TagGridRow): UnitTaggingRow {
    const tag: TagInformation = new TagInformation();
    tag.sequence = row.sequence;
    tag.tag = row.tag;
    tag.poLine = row.poLine;
    tag.riser = row.riser;
    tag.floor = row.floor;
    tag.room = row.room;
    tag.building = row.building;
    tag.reference = row.reference;
    tag.location = row.location;
    tag.size = row.size;
    tag.voltage = row.voltage;
    tag.poNum = row.poNum;
    tag.projName = row.projName;
    tag.noTag = row.noTag;
    const unit: UnitTaggingRow = new UnitTaggingRow();
    unit.lineId = row.lineId;
    unit.partNumber = row.lineDescription;
    unit.lineDescription = row.partNumber;
    unit.tagInformation = tag;
    return unit;
  }

  openSetReference(): void {
    this.openSetField('reference', 'Reference', 24);
  }

  openSetLocation(): void {
    this.openSetField('location', 'Location', 16);
  }

  private openSetField(target: string, targetLabel: string, maxLength: number): void {
    const dialogRef = this.dialog.open(SetTagFieldDialogComponent, {
      data: { target, targetLabel, maxLength, rows: this.gridData() },
    });
    dialogRef.afterClosed().subscribe((result: TagFieldUpdateResult) => {
      if (result) {
        this.applyBulkSet(target, maxLength, result);
      }
    });
  }

  private applyBulkSet(target: string, maxLength: number, result: TagFieldUpdateResult): void {
    for (const row of this.gridData()) {
      const targetIsBlank: boolean = StringUtils.isBlank((row as any)[target]);
      if (result.scope !== 'blank' || targetIsBlank) {
        (row as any)[target] = this.computeBulkValue(row, result).slice(0, maxLength);
      }
    }
    this.refreshTable();
    this.updateCounts();
  }

  private computeBulkValue(row: TagGridRow, result: TagFieldUpdateResult): string {
    let value = '';
    if (result.strategy === 'custom') {
      value = result.customValue || '';
    } else if (result.strategy === 'concat') {
      value = result.concatFields
        .map((field) => (row as any)[field])
        .filter((entry) => StringUtils.isNotBlank(entry))
        .join('/');
    } else {
      value = (row as any)[result.field] || '';
    }
    return value;
  }

  openTagPreview(): void {
    this.dialog.open(TagPreviewDialogComponent, { data: this.gridData() });
  }

  openHelp(): void {
    this.dialog.open(UnitTaggingHelpDialogComponent);
  }

  downloadTemplate(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.tagService.getTagTemplate(this.entityKey()).subscribe({
      next: (result: Blob) => {
        FileUtils.downloadFile(result, this.entityKey().entityId + '-tags.xlsx');
        this.isBusy.set(false);
      },
      error: (error) => this.handleError(error),
    });
  }

  uploadTemplate(files: FileList): void {
    if (files && files.length === 1) {
      const file = files[0];
      const reader = new FileReader();
      this.isBusy.set(true);
      this.errorMessage.set(undefined!);
      reader.onload = () => this.saveUploadedFile(this.buildUploadFile(file, reader.result as unknown as Uint8Array));
      reader.readAsArrayBuffer(file);
    }
  }

  private buildUploadFile(file: File, fileBytes: Uint8Array): UploadFile {
    const uploadFile: UploadFile = new UploadFile();
    uploadFile.entityKey = this.entityKey();
    uploadFile.fileBytes = fileBytes;
    uploadFile.fileName = file.name;
    uploadFile.documentName = file.name;
    uploadFile.mimeType = file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    uploadFile.documentType = 'Unit Information';
    return uploadFile;
  }

  private saveUploadedFile(uploadFile: UploadFile): void {
    this.tagService.saveTags(this.entityKey(), false, this.includeShippingTags(), uploadFile.fileBytes).subscribe({
      next: (response: TagSaveResponse) => this.handleUploadResponse(response, uploadFile),
      error: (error) => this.handleError(error),
    });
  }

  private handleUploadResponse(response: TagSaveResponse, uploadFile: UploadFile): void {
    if (response.level === MessageLevel.WARNING || response.level === MessageLevel.ERROR) {
      this.handleSaveResponse(response);
    } else {
      this.saveTagDocument(response, uploadFile);
    }
  }

  private saveTagDocument(response: TagSaveResponse, uploadFile: UploadFile): void {
    this.documentService.uploadFiles([uploadFile]).subscribe({
      next: () => this.handleSaveResponse(response),
      error: (error) => this.handleError(error),
    });
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  goBack(): void {
    if (this.entityKey() && this.entityKey().entityName) {
      const pageName: string = this.entityKey().entityName === 'QUOTE' ? 'quotes' : 'orders';
      this.router.navigate([pageName, this.entityKey().entityId, this.entityKey().entityRevision]);
    } else {
      this.router.navigate(['main']);
    }
  }
}
